import { useMemo, useState, type ReactElement } from 'react';
import type { LoadDay } from '@/lib/load';

/**
 * Gráfica SVG inline para PMC (CTL/ATL/TSB + TSS diario).
 *
 * Decisiones clave:
 *
 * 1. **Sin librerías externas** — toda la matemática (escalas, ticks,
 *    paths) se hace a mano. Mantiene el bundle ligero y es fácil de testear
 *    sin mocks de d3/recharts.
 *
 * 2. **Ejes Y duales independientes**:
 *    - Eje Y izquierdo  → TSS diario (barras grises).
 *      Escala: `[0, max(TSS) || 1] → [bottom, top]`.
 *    - Eje Y derecho   → CTL/ATL/TSB (líneas).
 *      Escala: `[min(min(CTL,ATL,TSB), 0), max(max(CTL,ATL,TSB), 0)] → [bottom, top]`.
 *      Forzamos que el 0 esté siempre en el dominio del eje derecho para que
 *      la línea TSB que cruza el cero (signo de la forma) sea visible.
 *
 * 3. **Ticks dinámicos en el eje X** — el contrato pide "cada 7-30 días
 *    según rango". Usamos heurística:
 *      ≤ 60d   → 7 días entre ticks
 *      ≤ 120d  → 14 días
 *      ≤ 240d  → 30 días
 *      > 240d  → 60 días
 *    Garantizamos al menos primer y último tick siempre presentes.
 *
 * 4. **Manejo de rango vacío** — si `days.length === 0` devolvemos un SVG
 *    placeholder accesible (no `null`) para que el contenedor padre conserve
 *    su layout y la a11y siga sirviendo `aria-label`.
 *
 * 5. **viewBox responsivo** — el SVG declara `viewBox="0 0 W H"` y
 *    `preserveAspectRatio="xMidYMid meet"`. El consumidor controla el tamaño
 *    final vía `style` o clases Tailwind del wrapper.
 *
 * 6. **Tooltip opcional** — `onMouseMove` sobre un rect transparente que
 *    cubre el área de plot. Calcula el índice del día por interpolación
 *    inversa de la X. Sin portales ni librerías; se renderiza como `<g>`
 *    encima del chart.
 */

export type LoadChartProps = {
  days: LoadDay[];
  width?: number;
  height?: number;
  /**
   * Etiqueta a11y. El componente expone un `aria-label` por defecto si no se
   * pasa, pero la página padre puede sobreescribirla con un texto más rico.
   */
  ariaLabel?: string;
};

// Paleta (alineada con WCAG AA contra fondo blanco)
const COLOR_CTL = '#0369a1'; // sky-700 — azul
const COLOR_ATL = '#c2410c'; // orange-700 — naranja
const COLOR_TSB_POS = '#15803d'; // green-700 — forma positiva
const COLOR_TSB_NEG = '#be123c'; // rose-700 — forma negativa
const COLOR_TSS_BAR = '#cbd5e1'; // slate-300 — barras TSS
const COLOR_AXIS = '#475569'; // slate-600
const COLOR_GRID = '#e2e8f0'; // slate-200

// Márgenes internos del SVG (espacio para ejes y leyenda)
const M = { top: 24, right: 56, bottom: 36, left: 48 };

const DEFAULT_W = 720;
const DEFAULT_H = 320;

export function LoadChart({
  days,
  width = DEFAULT_W,
  height = DEFAULT_H,
  ariaLabel,
}: LoadChartProps): ReactElement {
  // Hover state — null = nada resaltado.
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // Pre-cálculo de escalas + ticks. Memoizado: depende sólo de `days`/dims.
  const layout = useMemo(() => computeLayout(days, width, height), [days, width, height]);

  // Render vacío accesible: aún devolvemos SVG válido (no null) para que la
  // caja del padre no salte de tamaño entre estados.
  if (days.length === 0 || layout.plotW <= 0 || layout.plotH <= 0) {
    return (
      <svg
        role="img"
        aria-label={ariaLabel ?? 'Gráfica de carga sin datos'}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full"
        data-testid="load-chart-empty"
      >
        <rect x={0} y={0} width={width} height={height} fill="#f8fafc" />
        <text
          x={width / 2}
          y={height / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={COLOR_AXIS}
          fontSize={14}
        >
          Sin datos de carga en el rango seleccionado
        </text>
      </svg>
    );
  }

  const { plotW, plotH, xScale, yLeft, yRight, ticksX, ticksRight } = layout;

  // Paths CTL/ATL — líneas suavizadas a base de "L" rectos (sin bezier para
  // mantener el cálculo trivial y predecible en tests).
  const pathCTL = lineFor(days, xScale, (d) => yRight(d.ctl));
  const pathATL = lineFor(days, xScale, (d) => yRight(d.atl));

  // TSB: dibujamos línea sólida + área sombreada (signo por color).
  // Para que el área se vea correctamente cruzando el cero, partimos el área
  // en dos polígonos: uno positivo y otro negativo, recortando en y=0.
  const tsbZeroY = yRight(0);
  const tsbAreaPositive = areaFor(days, xScale, (d) => yRight(d.tsb), tsbZeroY, 'pos');
  const tsbAreaNegative = areaFor(days, xScale, (d) => yRight(d.tsb), tsbZeroY, 'neg');
  const tsbLine = lineFor(days, xScale, (d) => yRight(d.tsb));

  // Barras TSS — ancho proporcional al espaciado entre días (con un mínimo de 1px).
  const barWidth = Math.max(1, Math.min(8, plotW / Math.max(days.length, 1) - 1));

  return (
    <svg
      role="img"
      aria-label={
        ariaLabel ??
        `Gráfica PMC con ${days.length} días: CTL en azul, ATL en naranja, TSB sombreado por signo y barras grises de TSS diario.`
      }
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      className="w-full"
      data-testid="load-chart"
    >
      {/* Fondo del área de plot */}
      <rect
        x={M.left}
        y={M.top}
        width={plotW}
        height={plotH}
        fill="#ffffff"
        stroke={COLOR_GRID}
      />

      {/* Grid horizontal por ticks del eje derecho */}
      {ticksRight.map((t) => {
        const y = yRight(t);
        return (
          <line
            key={`gy-${t}`}
            x1={M.left}
            x2={M.left + plotW}
            y1={y}
            y2={y}
            stroke={COLOR_GRID}
            strokeDasharray="2 4"
          />
        );
      })}

      {/* Línea de cero (eje derecho) más marcada */}
      <line
        x1={M.left}
        x2={M.left + plotW}
        y1={tsbZeroY}
        y2={tsbZeroY}
        stroke={COLOR_AXIS}
        strokeWidth={1}
      />

      {/* Barras TSS (eje izquierdo) */}
      <g aria-hidden="true">
        {days.map((d, i) => {
          const x = xScale(i) - barWidth / 2;
          const y = yLeft(d.tss);
          const h = Math.max(0, M.top + plotH - y);
          const testIdProps = i === 0 ? { 'data-testid': 'tss-bar-first' } : {};
          return (
            <rect
              key={`bar-${d.dateISO}`}
              x={x}
              y={y}
              width={barWidth}
              height={h}
              fill={COLOR_TSS_BAR}
              {...testIdProps}
            />
          );
        })}
      </g>

      {/* TSB área (positivo + negativo) */}
      <g aria-hidden="true">
        {tsbAreaPositive && (
          <path d={tsbAreaPositive} fill={COLOR_TSB_POS} fillOpacity={0.18} />
        )}
        {tsbAreaNegative && (
          <path d={tsbAreaNegative} fill={COLOR_TSB_NEG} fillOpacity={0.18} />
        )}
        <path
          d={tsbLine}
          fill="none"
          stroke={COLOR_AXIS}
          strokeWidth={1.25}
          strokeDasharray="4 3"
          data-testid="line-tsb"
        />
      </g>

      {/* CTL + ATL líneas */}
      <path
        d={pathCTL}
        fill="none"
        stroke={COLOR_CTL}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
        data-testid="line-ctl"
      />
      <path
        d={pathATL}
        fill="none"
        stroke={COLOR_ATL}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
        data-testid="line-atl"
      />

      {/* Ejes y ticks */}
      <Axes
        plotW={plotW}
        plotH={plotH}
        yLeft={yLeft}
        yRight={yRight}
        ticksRight={ticksRight}
        ticksX={ticksX}
        days={days}
        xScale={xScale}
      />

      {/* Hover overlay + tooltip */}
      <rect
        x={M.left}
        y={M.top}
        width={plotW}
        height={plotH}
        fill="transparent"
        onMouseMove={(e) => {
          const rect = (e.currentTarget as SVGRectElement).getBoundingClientRect();
          // Mapear el clientX al sistema viewBox: el rect ya está escalado por
          // el viewBox, así que basta proporción + ancho de plot.
          const ratio = (e.clientX - rect.left) / Math.max(1, rect.width);
          const xViewBox = M.left + ratio * plotW;
          // Búsqueda inversa: índice cuyo xScale está más cerca.
          const idx = nearestIndex(xViewBox, days.length, xScale);
          setHoverIdx(idx);
        }}
        onMouseLeave={() => setHoverIdx(null)}
        data-testid="hover-overlay"
      />
      {hoverIdx !== null && hoverIdx >= 0 && hoverIdx < days.length && (
        <Tooltip
          day={days[hoverIdx] as LoadDay}
          x={xScale(hoverIdx)}
          plotTop={M.top}
          plotH={plotH}
          plotLeft={M.left}
          plotW={plotW}
        />
      )}

      {/* Leyenda */}
      <Legend width={width} />
    </svg>
  );
}

// ===========================================================================
// Sub-componentes internos
// ===========================================================================

function Axes({
  plotW,
  plotH,
  yLeft,
  yRight,
  ticksRight,
  ticksX,
  days,
  xScale,
}: {
  plotW: number;
  plotH: number;
  yLeft: (v: number) => number;
  yRight: (v: number) => number;
  ticksRight: number[];
  ticksX: number[]; // índices de día
  days: LoadDay[];
  xScale: (i: number) => number;
}): ReactElement {
  // Ticks Y izquierdo — derivados del max del TSS (4-5 marcas).
  const maxTss = Math.max(0, ...days.map((d) => d.tss));
  const ticksLeft = niceTicks(0, maxTss || 100, 4);
  return (
    <g aria-hidden="true" fontSize={11} fill={COLOR_AXIS}>
      {/* Eje X (línea base) */}
      <line
        x1={M.left}
        x2={M.left + plotW}
        y1={M.top + plotH}
        y2={M.top + plotH}
        stroke={COLOR_AXIS}
      />

      {/* Ticks X — fechas */}
      {ticksX.map((i) => {
        const day = days[i];
        if (!day) return null;
        const x = xScale(i);
        return (
          <g key={`tx-${day.dateISO}`}>
            <line x1={x} x2={x} y1={M.top + plotH} y2={M.top + plotH + 4} stroke={COLOR_AXIS} />
            <text x={x} y={M.top + plotH + 16} textAnchor="middle">
              {formatTickDate(day.dateISO)}
            </text>
          </g>
        );
      })}

      {/* Eje Y izquierdo (TSS) */}
      <line x1={M.left} x2={M.left} y1={M.top} y2={M.top + plotH} stroke={COLOR_AXIS} />
      {ticksLeft.map((t) => {
        const y = yLeft(t);
        return (
          <g key={`tyL-${t}`}>
            <line x1={M.left - 4} x2={M.left} y1={y} y2={y} stroke={COLOR_AXIS} />
            <text x={M.left - 6} y={y + 3} textAnchor="end">
              {Math.round(t)}
            </text>
          </g>
        );
      })}
      <text
        x={M.left - 36}
        y={M.top - 8}
        fontSize={10}
        fill={COLOR_AXIS}
        textAnchor="start"
      >
        TSS
      </text>

      {/* Eje Y derecho (CTL/ATL/TSB) */}
      <line
        x1={M.left + plotW}
        x2={M.left + plotW}
        y1={M.top}
        y2={M.top + plotH}
        stroke={COLOR_AXIS}
      />
      {ticksRight.map((t) => {
        const y = yRight(t);
        return (
          <g key={`tyR-${t}`}>
            <line
              x1={M.left + plotW}
              x2={M.left + plotW + 4}
              y1={y}
              y2={y}
              stroke={COLOR_AXIS}
            />
            <text x={M.left + plotW + 6} y={y + 3} textAnchor="start">
              {Math.round(t)}
            </text>
          </g>
        );
      })}
      <text
        x={M.left + plotW + 6}
        y={M.top - 8}
        fontSize={10}
        fill={COLOR_AXIS}
        textAnchor="start"
      >
        CTL / ATL / TSB
      </text>
    </g>
  );
}

function Legend({ width }: { width: number }): ReactElement {
  // Centrada arriba; tres entradas + TSS.
  const items: Array<{ color: string; label: string; dash?: boolean }> = [
    { color: COLOR_CTL, label: 'CTL' },
    { color: COLOR_ATL, label: 'ATL' },
    { color: COLOR_AXIS, label: 'TSB', dash: true },
    { color: COLOR_TSS_BAR, label: 'TSS' },
  ];
  const itemW = 70;
  const totalW = itemW * items.length;
  const startX = (width - totalW) / 2;
  return (
    <g fontSize={11} fill={COLOR_AXIS} aria-hidden="true">
      {items.map((it, i) => {
        const x = startX + i * itemW;
        return (
          <g key={it.label} transform={`translate(${x}, 6)`}>
            {it.label === 'TSS' ? (
              <rect x={0} y={2} width={14} height={8} fill={it.color} />
            ) : (
              <line
                x1={0}
                x2={14}
                y1={6}
                y2={6}
                stroke={it.color}
                strokeWidth={2}
                strokeDasharray={it.dash ? '4 3' : undefined}
              />
            )}
            <text x={18} y={10}>
              {it.label}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function Tooltip({
  day,
  x,
  plotTop,
  plotH,
  plotLeft,
  plotW,
}: {
  day: LoadDay;
  x: number;
  plotTop: number;
  plotH: number;
  plotLeft: number;
  plotW: number;
}): ReactElement {
  const boxW = 150;
  const boxH = 80;
  // Anclar el tooltip dentro del área de plot (no salir por los bordes).
  let bx = x + 8;
  if (bx + boxW > plotLeft + plotW) bx = x - boxW - 8;
  const by = plotTop + 8;
  return (
    <g pointerEvents="none" data-testid="load-chart-tooltip">
      <line
        x1={x}
        x2={x}
        y1={plotTop}
        y2={plotTop + plotH}
        stroke={COLOR_AXIS}
        strokeDasharray="2 2"
      />
      <rect
        x={bx}
        y={by}
        width={boxW}
        height={boxH}
        rx={4}
        fill="rgb(240 240 245)"
        fillOpacity={0.92}
      />
      <g fontSize={11} fill="#f8fafc">
        <text x={bx + 8} y={by + 16} fontWeight="600">
          {formatTickDate(day.dateISO)}
        </text>
        <text x={bx + 8} y={by + 32}>
          TSS: {Math.round(day.tss)}
        </text>
        <text x={bx + 8} y={by + 46}>
          CTL: {day.ctl.toFixed(1)}
        </text>
        <text x={bx + 8} y={by + 60}>
          ATL: {day.atl.toFixed(1)}
        </text>
        <text x={bx + 8} y={by + 74}>
          TSB: {day.tsb.toFixed(1)}
        </text>
      </g>
    </g>
  );
}

// ===========================================================================
// Helpers puros (sin estado) — exportados con `_` para testear si hace falta
// ===========================================================================

function computeLayout(days: LoadDay[], width: number, height: number) {
  const plotW = width - M.left - M.right;
  const plotH = height - M.top - M.bottom;

  // X: índice → coord. Si sólo hay un día, centramos.
  const xScale = (i: number) => {
    if (days.length <= 1) return M.left + plotW / 2;
    return M.left + (i / (days.length - 1)) * plotW;
  };

  // Y izquierdo (TSS): [0, maxTss or 1] → [bottom, top]
  const maxTss = Math.max(1, ...days.map((d) => d.tss));
  const yLeft = (v: number) => {
    const ratio = Math.max(0, Math.min(1, v / maxTss));
    return M.top + plotH - ratio * plotH;
  };

  // Y derecho (CTL/ATL/TSB): dominio forzado a incluir 0.
  const allRight = days.flatMap((d) => [d.ctl, d.atl, d.tsb]);
  const minR = Math.min(0, ...allRight);
  const maxR = Math.max(0, ...allRight, 1);
  const span = Math.max(1, maxR - minR);
  const yRight = (v: number) => {
    const ratio = (v - minR) / span;
    return M.top + plotH - ratio * plotH;
  };

  // Ticks Y derecho: 5 marcas "bonitas"
  const ticksRight = niceTicks(minR, maxR, 5);
  // Ticks X dinámicos según rango
  const ticksX = pickXTickIndices(days.length);

  return { plotW, plotH, xScale, yLeft, yRight, ticksRight, ticksX };
}

function lineFor(
  days: LoadDay[],
  xScale: (i: number) => number,
  yOf: (d: LoadDay) => number,
): string {
  if (days.length === 0) return '';
  const parts: string[] = [];
  days.forEach((d, i) => {
    const cmd = i === 0 ? 'M' : 'L';
    parts.push(`${cmd}${xScale(i).toFixed(2)},${yOf(d).toFixed(2)}`);
  });
  return parts.join(' ');
}

/**
 * Polígono cerrado entre la curva y la línea base `yBase` (eje cero).
 * `mode='pos'` mantiene sólo los segmentos donde y(d) <= yBase (arriba del cero
 * en coords SVG, valor positivo); `mode='neg'` los de abajo. Para los puntos
 * que no entran en el modo, los clampeamos al yBase para que el polígono
 * permanezca cerrado y plano en esa zona — evita huecos.
 */
function areaFor(
  days: LoadDay[],
  xScale: (i: number) => number,
  yOf: (d: LoadDay) => number,
  yBase: number,
  mode: 'pos' | 'neg',
): string | null {
  if (days.length === 0) return null;
  const has = days.some((d) => (mode === 'pos' ? yOf(d) <= yBase : yOf(d) >= yBase));
  if (!has) return null;
  const top: string[] = [];
  days.forEach((d, i) => {
    let y = yOf(d);
    if (mode === 'pos' && y > yBase) y = yBase;
    if (mode === 'neg' && y < yBase) y = yBase;
    const cmd = i === 0 ? 'M' : 'L';
    top.push(`${cmd}${xScale(i).toFixed(2)},${y.toFixed(2)}`);
  });
  // Cerrar por debajo/encima sobre la línea base.
  const last = xScale(days.length - 1).toFixed(2);
  const first = xScale(0).toFixed(2);
  top.push(`L${last},${yBase.toFixed(2)}`);
  top.push(`L${first},${yBase.toFixed(2)}`);
  top.push('Z');
  return top.join(' ');
}

function nearestIndex(
  xViewBox: number,
  n: number,
  xScale: (i: number) => number,
): number {
  if (n <= 0) return -1;
  if (n === 1) return 0;
  // O(n) — n máximo ~365, irrelevante.
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < n; i++) {
    const d = Math.abs(xScale(i) - xViewBox);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/**
 * Heurística de ticks X según el número de días del rango.
 * Garantiza primer y último día siempre presentes.
 */
function pickXTickIndices(n: number): number[] {
  if (n <= 0) return [];
  if (n === 1) return [0];
  let step: number;
  if (n <= 60) step = 7;
  else if (n <= 120) step = 14;
  else if (n <= 240) step = 30;
  else step = 60;
  const out: number[] = [];
  for (let i = 0; i < n; i += step) out.push(i);
  // Asegurar el último.
  const lastIdx = n - 1;
  if (out[out.length - 1] !== lastIdx) {
    // Evitar pegar dos ticks demasiado juntos al final.
    if (lastIdx - (out[out.length - 1] ?? 0) < step / 2) {
      out[out.length - 1] = lastIdx;
    } else {
      out.push(lastIdx);
    }
  }
  return out;
}

/**
 * Genera ~`target` ticks "bonitos" en [min,max]. Algoritmo simplificado de
 * d3-array's `ticks()`: escoge el paso ∈ {1, 2, 5, 10} * 10^k más cercano.
 */
function niceTicks(min: number, max: number, target: number): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return [min];
  }
  const range = max - min;
  const rawStep = range / target;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  let step: number;
  if (norm < 1.5) step = 1 * mag;
  else if (norm < 3) step = 2 * mag;
  else if (norm < 7) step = 5 * mag;
  else step = 10 * mag;
  const start = Math.ceil(min / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= max + 1e-9; v += step) {
    // Redondeo defensivo contra ruido FP.
    ticks.push(Math.round(v * 1e6) / 1e6);
  }
  return ticks;
}

function formatTickDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}
